import { HankoDialog } from "../../../../components/HankoDialog";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import React, { useContext, useEffect } from "react";
import { ApiClient } from "../../../../client/ApiClient";
import { CustomClaimType, Project } from "../../../../models/Project";
import { FormButtons } from "../../../../components/form/FormButtons";
import { SnackbarContext } from "../../../../components/Snackbar/SnackbarProvider";
import { useRevalidator } from "react-router-dom";

interface Props {
  open: boolean
  onClose: () => void
  organizationId: string
  project: Project
  name?: string
}

type State = {
  name: string
  type: CustomClaimType
  description: string
}

const claimTypes: CustomClaimType[] = ["string", "number", "boolean", "string_list"]

const emptyState: State = { name: "", type: "string", description: "" }

// Mirrors Hanko's own customClaimNamePattern (config/config_custom_claims.go) - names must stay
// safe for gjson path lookups, which treat '.', '|', '#', '@', '*', '?' (and, not enforced here
// since it'd otherwise be allowed, whitespace) as meaningful metacharacters. Hand-kept in sync;
// the console has no shared validation module with the Go backend.
const CUSTOM_CLAIM_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/

// Mirrors Hanko's reservedCustomClaimNames (config/config_custom_claims.go) - compared
// case-insensitively there, so this list is lowercase to match directly.
const RESERVED_CUSTOM_CLAIM_NAMES = new Set(["sub", "iat", "exp", "aud", "iss", "email", "username", "session_id"])

const validateClaimName = (name: string, existingNames: string[]): string | undefined => {
  if (name === "") {
    return undefined
  }
  const trimmed = name.trim()
  if (!CUSTOM_CLAIM_NAME_PATTERN.test(trimmed)) {
    return "Must start with a letter, and contain only letters, digits and underscores (e.g. orgRole, org_role) - no spaces or other symbols."
  }
  if (RESERVED_CUSTOM_CLAIM_NAMES.has(trimmed.toLowerCase())) {
    return `"${trimmed}" is a reserved name and can't be used for a custom claim.`
  }
  if (existingNames.includes(trimmed)) {
    return `A claim named "${trimmed}" already exists.`
  }
  return undefined
}

const CUSTOM_CLAIM_DESCRIPTION_MAX_LENGTH = 128

const validateClaimDescription = (description: string): string | undefined => {
  if (description.trim().length > CUSTOM_CLAIM_DESCRIPTION_MAX_LENGTH) {
    return `Must be ${CUSTOM_CLAIM_DESCRIPTION_MAX_LENGTH} characters or fewer.`
  }
  return undefined
}

export const CustomClaimModal = (props: Props) => {
  const { name, open, onClose, organizationId, project } = props
  const [isLoading, setIsLoading] = React.useState(false)
  const revalidator = useRevalidator()
  const { error } = useContext(SnackbarContext)
  const [state, setState] = React.useState<State>(emptyState)
  const [nameError, setNameError] = React.useState<string | undefined>(undefined)
  const [descriptionError, setDescriptionError] = React.useState<string | undefined>(undefined)

  useEffect(() => {
    if (name !== undefined && project.custom_claims?.[name] !== undefined) {
      const definition = project.custom_claims[name]
      setState({
        name,
        type: definition.type,
        description: definition.description ?? "",
      })
    } else {
      setState(emptyState)
    }
    setNameError(undefined)
    setDescriptionError(undefined)
  }, [name, open]);

  const submit = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    const existingNames = Object.keys(project.custom_claims ?? {}).filter((n) => n !== name)
    const nameErr = validateClaimName(state.name, existingNames)
    const descriptionErr = validateClaimDescription(state.description)
    setNameError(nameErr)
    setDescriptionError(descriptionErr)
    if (nameErr !== undefined || descriptionErr !== undefined) {
      return
    }

    setIsLoading(true)
    try {
      const trimmedName = state.name.trim()
      const customClaims = { ...project.custom_claims }
      if (name !== undefined && name !== trimmedName) {
        delete customClaims[name]
      }
      customClaims[trimmedName] = {
        type: state.type,
        description: state.description.trim() === "" ? undefined : state.description.trim(),
      }

      await ApiClient.updateProject(organizationId, project.id, {
        custom_claims: customClaims,
      })
      revalidator.revalidate()
      closeModal()
    } catch (e) {
      error("Failed to save custom claim")
      console.log(e)
    }
    setIsLoading(false)
  }

  const closeModal = () => {
    setState(emptyState)
    onClose()
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    })
  }

  return <HankoDialog open={open} onClose={onClose}>
    <Box maxWidth="500px" sx={{ display: "flex", gap: "20px", flexDirection: 'column', width: '500px' }}>
      <Typography variant="h4">{name !== undefined ? "Edit custom claim" : "New custom claim"}</Typography>
      <form onSubmit={submit}>
        <Box sx={{ display: "flex", gap: "20px", flexDirection: 'column' }}>
          <TextField label="Name" name="name" fullWidth onChange={onChange} required
                     disabled={name !== undefined} value={state.name}
                     error={nameError !== undefined}
                     helperText={nameError ?? "Letters, digits and underscores only, must start with a letter (e.g. orgRole, org_role)."} />
          <TextField select label="Type" name="type" fullWidth onChange={onChange} required value={state.type}
                     helperText={name !== undefined ? "Doesn't retype values already stored for users - each keeps its old type until next resolved (e.g. their next login via the mapping connection)." : undefined}>
            {claimTypes.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
          </TextField>
          <TextField label="Description" name="description" fullWidth onChange={onChange} value={state.description}
                     error={descriptionError !== undefined} helperText={descriptionError} />
          <FormButtons onCancel={() => closeModal()} loading={isLoading} disabled={false}
                       submitButtonText={name !== undefined ? "Save" : "Add claim"} />
        </Box>
      </form>
    </Box>
  </HankoDialog>
}
